/**
 * StickyPurchaseBar — bottom-anchored purchase CTA
 * (issue #434 acceptance criterion #7).
 *
 * Shows the selected plan price + "Start <plan> plan" CTA +
 * "Cancel anytime" assurance. The bar uses a frosted-glass
 * effect (`backdrop-filter: blur(12px)`) and slides up when
 * the user scrolls past the plan cards.
 *
 * The bar has its own close affordance (×) so users who
  * want to browse the FAQ aren't being prompted. The close state
  * is owned by the parent so it persists within a session, but
  * resets the moment the user re-selects a plan (see
  * PaywallScreen.handleCadenceSelect) — the dismissed prop
  * documents the "hide until they re-select" contract.
  */
import { useEffect, useRef, type ReactElement } from 'react';
import type { PlanCadence, PlanCardContent } from '../lib/paywall/planContent';

interface Props {
  /** The plan currently selected. */
  plan: PlanCardContent;
  /** True once the user has scrolled past the plan-cards section. */
  visible: boolean;
  /** True if the user dismissed the bar; we hide until they re-select. */
  dismissed: boolean;
  onDismiss: () => void;
  busy: boolean;
  onPurchase: () => void;
  /** Cadence selection change \u2014 forwarded so the bar's CTA label matches. */
  onSelect: (cadence: PlanCadence) => void;
}

export function StickyPurchaseBar({
  plan,
  visible,
  dismissed,
  onDismiss,
  busy,
  onPurchase,
  onSelect,
}: Props): ReactElement | null {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // The parent owns the visible state; this hook is a safety net
  // so the bar doesn't drift if the plan-cards section resizes
  // (e.g. an FAQ row opens). Nothing to do here in v1 \u2014 the
  // parent re-evaluates on scroll. Keeping the ref reserved
  // for the future IntersectionObserver wiring.
  useEffect(() => {
    void sentinelRef;
  }, []);

  if (dismissed) return null;

  const ctaLabel =
    busy
      ? 'Opening Store\u2026'
      : plan.cadence === 'annual'
        ? 'Start annual plan'
        : 'Start monthly plan';

  return (
    <div
      className="sticky-purchase-bar"
      role="region"
      aria-label="Purchase summary"
      data-testid="sticky-purchase-bar"
      data-visible={visible ? 'true' : 'false'}
    >
      <div className="sticky-purchase-bar-inner">
        <div className="sticky-purchase-bar-price">
          <span className="sticky-purchase-bar-amount">{plan.priceLabel}</span>
          <span className="sticky-purchase-bar-cadence">
            {plan.cadenceLabel}
          </span>
          <span className="sticky-purchase-bar-assurance">
            Cancel anytime
          </span>
        </div>
        <div className="sticky-purchase-bar-actions">
          <select
            className="sticky-purchase-bar-select"
            value={plan.cadence}
            onChange={(event) =>
              onSelect(event.target.value as PlanCadence)
            }
            aria-label="Switch plan cadence"
          >
            <option value="annual">Annual</option>
            <option value="monthly">Monthly</option>
          </select>
          <button
            type="button"
            className="snap-button"
            disabled={busy}
            onClick={onPurchase}
            data-testid="sticky-purchase-bar-cta"
          >
            {ctaLabel}
          </button>
          <button
            type="button"
            className="sticky-purchase-bar-dismiss"
            aria-label="Dismiss purchase bar"
            onClick={onDismiss}
          >
            \u00d7
          </button>
        </div>
      </div>
      <div ref={sentinelRef} hidden />
    </div>
  );
}